import React, { useState, useEffect, useCallback } from 'react';
import { remindersAPI, familyMembersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Bell,
  Plus,
  Trash2,
  Calendar,
  Clock,
  Pill,
  Syringe,
  ClipboardList,
  CheckCircle,
  Loader2,
  Edit,
  Users,
  User,
  RefreshCw,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const REMINDER_TYPES = [
  { value: 'medication', label: 'Médicament', icon: Pill, color: 'bg-green-500' },
  { value: 'appointment', label: 'Rendez-vous', icon: Calendar, color: 'bg-blue-500' },
  { value: 'exam', label: 'Examen', icon: ClipboardList, color: 'bg-amber-500' },
  { value: 'vaccination', label: 'Vaccination', icon: Syringe, color: 'bg-purple-500' },
  { value: 'other', label: 'Autre', icon: Bell, color: 'bg-slate-500' }
];

const REPEAT_OPTIONS = [
  { value: 'none', label: 'Une seule fois' },
  { value: 'daily', label: 'Tous les jours' },
  { value: 'weekly', label: 'Chaque semaine' },
  { value: 'monthly', label: 'Chaque mois' }
];

const MedicalRemindersPage = () => {
  const { user } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  const [form, setForm] = useState({
    title: '',
    description: '',
    reminder_type: 'medication',
    reminder_date: '',
    reminder_time: '09:00',
    family_member_id: '',
    repeat: 'none',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [showCompleted]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [remindersRes, familyRes] = await Promise.all([
        remindersAPI.getAll(null, showCompleted),
        familyMembersAPI.getAll()
      ]);
      setReminders(remindersRes.data.reminders || []);
      setFamilyMembers(familyRes.data.members || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des rappels');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      reminder_type: 'medication',
      reminder_date: '',
      reminder_time: '09:00',
      family_member_id: '',
      repeat: 'none',
      notes: ''
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title || !form.reminder_date) {
      toast.error('Veuillez remplir le titre et la date');
      return;
    }

    setSaving(true);
    try {
      await remindersAPI.create({
        ...form,
        family_member_id: form.family_member_id || null,
        repeat: form.repeat === 'none' ? null : form.repeat
      });
      toast.success('Rappel créé avec succès');
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedReminder) return;

    setSaving(true);
    try {
      await remindersAPI.update(selectedReminder.id, {
        ...form,
        repeat: form.repeat === 'none' ? null : form.repeat
      });
      toast.success('Rappel mis à jour');
      setShowEditModal(false);
      setSelectedReminder(null);
      loadData();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce rappel ?')) return;

    try {
      await remindersAPI.delete(id);
      toast.success('Rappel supprimé');
      setReminders(reminders.filter(r => r.id !== id));
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleComplete = async (id) => {
    try {
      await remindersAPI.complete(id);
      toast.success('Rappel marqué comme terminé');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openEditModal = (reminder) => {
    setSelectedReminder(reminder);
    setForm({
      title: reminder.title,
      description: reminder.description || '',
      reminder_type: reminder.reminder_type,
      reminder_date: reminder.reminder_date,
      reminder_time: reminder.reminder_time || '09:00',
      family_member_id: reminder.family_member_id || '',
      repeat: reminder.repeat || 'none',
      notes: reminder.notes || ''
    });
    setShowEditModal(true);
  };

  const getTypeInfo = (type) => {
    return REMINDER_TYPES.find(t => t.value === type) || REMINDER_TYPES[4];
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (date.toDateString() === today.toDateString()) {
        return "Aujourd'hui";
      } else if (date.toDateString() === tomorrow.toDateString()) {
        return 'Demain';
      }
      return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  const groupedReminders = reminders.reduce((acc, reminder) => {
    const date = reminder.reminder_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(reminder);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedReminders).sort();

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6 w-full max-w-full overflow-x-hidden" data-testid="reminders-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Rappels médicaux
          </h1>
          <p className="text-slate-500 mt-1">
            Ne manquez plus vos rendez-vous et traitements
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowCompleted(!showCompleted)}
            className={`rounded-xl ${showCompleted ? 'border-[#1976D2] text-[#1976D2]' : ''}`}
          >
            {showCompleted ? 'Masquer terminés' : 'Voir terminés'}
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-xl"
            data-testid="create-reminder-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouveau rappel
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {REMINDER_TYPES.map((type) => {
          const count = reminders.filter(r => r.reminder_type === type.value && r.is_active).length;
          return (
            <Card key={type.value} className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${type.color} flex items-center justify-center`}>
                  <type.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{count}</p>
                  <p className="text-xs text-slate-500">{type.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Reminders List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
        </div>
      ) : sortedDates.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="text-center py-12">
            <Bell className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">Aucun rappel</h3>
            <p className="text-slate-500 mt-2 mb-4">
              Créez votre premier rappel pour ne plus oublier vos rendez-vous
            </p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-xl"
            >
              <Plus className="w-5 h-5 mr-2" />
              Créer un rappel
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {sortedDates.map(date => (
            <div key={date}>
              <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase">
                {formatDate(date)}
              </h3>
              <div className="space-y-3">
                {groupedReminders[date].map(reminder => {
                  const typeInfo = getTypeInfo(reminder.reminder_type);
                  return (
                    <Card 
                      key={reminder.id} 
                      className={`border-0 shadow-sm transition-all hover:shadow-md ${!reminder.is_active ? 'opacity-60' : ''}`}
                      data-testid={`reminder-${reminder.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl ${typeInfo.color} flex items-center justify-center flex-shrink-0`}>
                            <typeInfo.icon className="w-6 h-6 text-white" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h3 className="font-semibold text-slate-900">{reminder.title}</h3>
                              {reminder.repeat && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  {REPEAT_OPTIONS.find(o => o.value === reminder.repeat)?.label}
                                </Badge>
                              )}
                              {reminder.family_member_name && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">
                                  <Users className="w-3 h-3 mr-1" />
                                  {reminder.family_member_name}
                                </Badge>
                              )}
                              {!reminder.is_active && (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Terminé
                                </Badge>
                              )}
                            </div>
                            
                            {reminder.description && (
                              <p className="text-sm text-slate-500 mb-2">{reminder.description}</p>
                            )}
                            
                            <div className="flex items-center gap-4 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {reminder.reminder_time}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${typeInfo.color} text-white`}>
                                {typeInfo.label}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            {reminder.is_active && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleComplete(reminder.id)}
                                className="text-green-500 hover:text-green-700 hover:bg-green-50"
                                title="Marquer comme terminé"
                              >
                                <CheckCircle className="w-5 h-5" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditModal(reminder)}
                              className="text-slate-500 hover:text-[#1976D2]"
                            >
                              <Edit className="w-5 h-5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(reminder.id)}
                              className="text-slate-500 hover:text-red-600"
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nouveau rappel</DialogTitle>
            <DialogDescription>
              Créez un rappel pour ne pas oublier vos rendez-vous ou traitements
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Ex: Prendre mes vitamines"
                data-testid="reminder-title"
              />
            </div>
            
            <div>
              <Label>Type</Label>
              <Select
                value={form.reminder_type}
                onValueChange={(v) => setForm(f => ({ ...f, reminder_type: v }))}
              >
                <SelectTrigger data-testid="reminder-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REMINDER_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={form.reminder_date}
                  onChange={(e) => setForm(f => ({ ...f, reminder_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="reminder-date"
                />
              </div>
              <div>
                <Label>Heure</Label>
                <Input
                  type="time"
                  value={form.reminder_time}
                  onChange={(e) => setForm(f => ({ ...f, reminder_time: e.target.value }))}
                  data-testid="reminder-time"
                />
              </div>
            </div>
            
            <div>
              <Label>Répétition</Label>
              <Select
                value={form.repeat}
                onValueChange={(v) => setForm(f => ({ ...f, repeat: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Une seule fois" />
                </SelectTrigger>
                <SelectContent>
                  {REPEAT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value || 'none'} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {familyMembers.length > 0 && (
              <div>
                <Label>Pour qui ?</Label>
                <Select
                  value={form.family_member_id || 'me'}
                  onValueChange={(v) => setForm(f => ({ ...f, family_member_id: v === 'me' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="me">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Moi-même
                      </div>
                    </SelectItem>
                    {familyMembers.map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          {member.full_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Instructions supplémentaires..."
                rows={2}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 rounded-xl"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#E53935] hover:bg-[#C62828] rounded-xl"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier le rappel</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Type</Label>
              <Select
                value={form.reminder_type}
                onValueChange={(v) => setForm(f => ({ ...f, reminder_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REMINDER_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={form.reminder_date}
                  onChange={(e) => setForm(f => ({ ...f, reminder_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Heure</Label>
                <Input
                  type="time"
                  value={form.reminder_time}
                  onChange={(e) => setForm(f => ({ ...f, reminder_time: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Répétition</Label>
              <Select
                value={form.repeat}
                onValueChange={(v) => setForm(f => ({ ...f, repeat: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Une seule fois" />
                </SelectTrigger>
                <SelectContent>
                  {REPEAT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value || 'none'} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="flex-1 rounded-xl"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#E53935] hover:bg-[#C62828] rounded-xl"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicalRemindersPage;
